<template>
  <div class="page-content">
    <OrganismsLoading v-show="isLoading || !isGetProducts" />
    <div v-if="isLoading" style="height: 10000px"></div>
    <PagePartsSet
      v-else
      v-show="isGetProducts"
      ref="pagePartsSetRef"
      :page-parts="pagePartsRef"
      @fetched-product-list.once="
        (v) => {
          createJsonLD(v)
          isGetProducts = true
        }
      "
    />
  </div>
</template>

<script setup lang="ts">
import { PagePartsFragmentFragment } from "~/types/pageParts"
import { usePublicProductCategoryFindOneQuery } from "~/gql/urql.generated"
import { PublicProductCategoryFindOneQueryVariables, ProductCategory } from "~/types/type.generated"
import { ScriptPropertySrc, ScriptPropertyText } from "~/types/meta"
import {
  createItemListJsonLD,
  createLdJson,
  createProductJsonLDByList,
  useCustomMeta,
  createBreadcrumbJsonLDFromPageParts,
} from "~/composables/meta"
import { ApiPageOneAbsoluteUrlResponsePage } from "~/types/server/api/page"
import type { ApiProductSearchResponse_Product } from "~/types/server/api/product"
import { useSocial } from "~/composables/useSocial"
import PagePartsSet from "~/components/pageParts/Set.vue"
definePageMeta({ layout: "default-with-extended-footer" })

const route = useRoute()
const customMeta = useCustomMeta()
const isLoading = useState<boolean>(() => true)
const pageRef = ref<ApiPageOneAbsoluteUrlResponsePage>()
const pagePartsRef = ref<PagePartsFragmentFragment[]>([])
const isGetProducts = ref(false)
const pagePartsSetRef = ref<InstanceType<typeof PagePartsSet> | null>(null)

const { fetchWithHandleRedirect } = usePageOneApi()
const absoluteUrl = `${route.params.categorySlug}/${route.params.subCategorySlug}`
const pageOneResponse = await fetchWithHandleRedirect(absoluteUrl, route)
pageRef.value = pageOneResponse.page
// TODO: 型修正
pagePartsRef.value = pageOneResponse.page_parts as PagePartsFragmentFragment[]

const productCategoryVariables = reactive<PublicProductCategoryFindOneQueryVariables>({
  filter: {
    document_id: "",
  },
})

const { executeQuery: executeQueryProductCategoryFindOne } = await usePublicProductCategoryFindOneQuery({
  requestPolicy: "cache-first",
  variables: productCategoryVariables,
  pause: true,
})

const { marketingTagsEvent } = useSocial()

// page取得後にpagePartsの取得
if (pageRef.value) {
  await useAsyncData(
    "usePublicPagePartsManyQueryForSubCategory",
    async () => {
      isLoading.value = true

      const { setProducts } = useFetchProductForPageParts()
      await setProducts(pagePartsRef.value)
      marketingTagsEvent(pagePartsRef.value)

      if (pagePartsRef.value.length > 0) {
        for await (const pageParts of pagePartsRef.value) {
          if (pageParts.category_id) {
            // fetch category
            productCategoryVariables.filter = {
              document_id: pageParts.category_id,
              is_status: true,
            }

            if (executeQueryProductCategoryFindOne().isPaused) await executeQueryProductCategoryFindOne().resume()
            const { data } = await executeQueryProductCategoryFindOne()

            if (data?.value?.publicProductCategoryFindOne) {
              pageParts.category = data?.value?.publicProductCategoryFindOne as ProductCategory
            }
          }
        }
      }

      isLoading.value = false
    },
    { lazy: true },
  )
}

// meta script
const metaData = computed(() => {
  const title = pageRef.value?.title || ""
  const description = pageRef.value?.description || ""
  const image = pageRef.value?.ogp_url || ""
  const noindex = pageRef.value?.noindex
  return {
    title,
    description,
    image,
    noindex,
  }
})

// 本来ならuseHeadと同じタイミングでJsonLDも追加したいがproductList取得まで待てないのであとで追加
const createJsonLD = (products: ApiProductSearchResponse_Product[]) => {
  const jsonLDScript = createLdJson([
    createBreadcrumbJsonLDFromPageParts(pagePartsRef.value),
    createItemListJsonLD({
      count: products.length,
      items: products.map((product) => createProductJsonLDByList(product, pageRef.value?.description)),
    }),
  ])

  if (jsonLDScript) {
    useHead({
      script: [jsonLDScript],
    })
  }
}

const metaScript = computed(() => {
  const script: (ScriptPropertyText | ScriptPropertySrc)[] = []
  const felmatTag = { src: "https://js.crossees.com/csslp.js" }
  const linkATag = { src: "https://link-ag.net/dist/p/l/index.js" }
  script.push(felmatTag)
  script.push(linkATag)
  return script
})

useHead(
  customMeta.fetch({
    title: `${metaData.value.title}`,
    description: `${metaData.value.description}`,
    image: `${metaData.value.image}`,
    script: metaScript.value,
    robots: metaData.value.noindex ? "noindex" : undefined,
  }),
)
</script>

<style scoped module lang="scss">
.skeleton_loading {
  @include container-lg;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 2rem;
  margin-top: 2rem;
  padding: 0 0.5rem;
  .skeleton_loading_item {
    width: 30%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    .image {
      @include background-skeleton-loading;
      aspect-ratio: 184 / 245;
    }
  }
}
</style>
